import { TYPEAHEAD_STREAMING_BATCH_UPDATE } from 'actions/actionConstants'
import { fetchBatchPlacesByIds } from 'actions/PlaceActions'
import type { TypeaheadStreamingFieldUpdate } from 'reducers/typeaheadStreamingUpdateUtils'
import type { AppAction } from 'actions/types/ActionTypes'

type Dispatch = (action: AppAction) => void

export interface TypeaheadStreamBatcher {
  enqueue: (entry: TypeaheadStreamingFieldUpdate, placeIds?: Array<string>) => void
  flush: () => void
  cancelPending: () => void
}

const typeaheadStreamBatchers = new Map<string, TypeaheadStreamBatcher>()

function scheduleFlush(flush: () => void): number | ReturnType<typeof setTimeout> {
  if (typeof requestAnimationFrame !== 'undefined') {
    return requestAnimationFrame(flush)
  }
  return setTimeout(flush, 0)
}

function cancelScheduledFlush(rafId: number | ReturnType<typeof setTimeout> | undefined): void {
  if (rafId === undefined) return

  if (typeof rafId === 'number' && typeof cancelAnimationFrame !== 'undefined') {
    cancelAnimationFrame(rafId)
    return
  }

  clearTimeout(rafId as ReturnType<typeof setTimeout>)
}

function createTypeaheadStreamBatcher(typeaheadId: string, dispatch: Dispatch): TypeaheadStreamBatcher {
  const buffer: Array<TypeaheadStreamingFieldUpdate> = []
  const pendingPlaceIds = new Set<string>()
  let rafId: number | ReturnType<typeof setTimeout> | undefined

  const flush = () => {
    cancelScheduledFlush(rafId)
    rafId = undefined

    if (buffer.length === 0 && pendingPlaceIds.size === 0) return

    const batch = buffer.splice(0)
    const placeIds = Array.from(pendingPlaceIds)
    pendingPlaceIds.clear()

    if (batch.length > 0) {
      dispatch({
        type: TYPEAHEAD_STREAMING_BATCH_UPDATE,
        typeaheadId,
        batch,
      })
    }
    if (placeIds.length > 0) {
      dispatch(fetchBatchPlacesByIds(placeIds))
    }
  }

  const scheduleFlushIfNeeded = () => {
    if (rafId !== undefined) return
    rafId = scheduleFlush(flush)
  }

  const enqueue = (entry: TypeaheadStreamingFieldUpdate, placeIds?: Array<string>) => {
    buffer.push(entry)
    if (placeIds) {
      for (const id of placeIds) {
        pendingPlaceIds.add(id)
      }
    }
    scheduleFlushIfNeeded()
  }

  const cancelPending = () => {
    cancelScheduledFlush(rafId)
    rafId = undefined
    buffer.length = 0
    pendingPlaceIds.clear()
  }

  return { enqueue, flush, cancelPending }
}

export function getOrCreateTypeaheadStreamBatcher(typeaheadId: string, dispatch: Dispatch): TypeaheadStreamBatcher {
  const existing = typeaheadStreamBatchers.get(typeaheadId)
  if (existing) return existing

  const batcher = createTypeaheadStreamBatcher(typeaheadId, dispatch)
  typeaheadStreamBatchers.set(typeaheadId, batcher)
  return batcher
}

export function removeTypeaheadStreamBatcher(typeaheadId: string): void {
  const batcher = typeaheadStreamBatchers.get(typeaheadId)
  if (!batcher) return

  batcher.cancelPending()
  typeaheadStreamBatchers.delete(typeaheadId)
}
